import { createSlice, PayloadAction, type Draft } from "@reduxjs/toolkit";
import { v4 as uuidv4 } from "uuid";

import {
  type TDefectListOption,
  blankDefectListOptions,
  SettingsKey,
  TDefectListOptions,
  TemplatePageType,
  TSectionPage,
  TTemplateContent,
  TImageManifestEntry,
} from "models";
import { defaultPageFooter, defaultPageHeader } from "utils/templates/default-cover-sheet";
import { ensureMandatorySections } from "utils/templates/mandatory-sections";
import { seedNewTemplate, customSectionStarter } from "utils/templates/seed-new-template";
import { migrateLegacyImageReferences } from "utils/templates/image-manifest";
import { Content } from "@tiptap/react";

/** Define a type for the slice state */
type _TemplateBuilderState = {
  documentId: string | undefined;
  templateName: string;
  logoUrl: string;
  storageToken: string;
  pages: TSectionPage[];
  selectedPageIndex: number;
  defaultOptions: TDefectListOptions;
  settings: {
    reportHeader: Content;
    reportFooter: Content;
  };
  /** Defines the initial editor text. */
  editorInit: Content | undefined;
  headerEditorInit: Content | undefined;
  footerEditorInit: Content | undefined;
  loading: boolean;
  isSaving: boolean;
  showSettings: boolean;
  showCoverPicker: boolean;
  showWelcome: boolean;
  version: number;
  /** Recently-used colours (most recent first) for the colour picker swatches — persisted on the document. */
  recentColors: string[];
  /** Every image referenced anywhere in `pages` — see `TImageManifestEntry`. */
  imageManifest: TImageManifestEntry[];
  /** Document schema version. New/already-migrated templates are always `1`. */
  schema: number;
};

/** Swatch row shows at most this many recent colours (two rows of 6). */
export const kMaxRecentColors = 12;

/** Defines the initial state using that type */
const initialState: _TemplateBuilderState = {
  documentId: undefined,
  templateName: "Report Template",
  logoUrl: "",
  storageToken: "",
  selectedPageIndex: 0,
  pages: [],
  defaultOptions: blankDefectListOptions,
  settings: {
    reportHeader: [],
    reportFooter: [],
  },
  editorInit: [],
  headerEditorInit: [],
  footerEditorInit: [],
  loading: true,
  isSaving: false,
  showSettings: false,
  showCoverPicker: false,
  showWelcome: false,
  version: 1,
  recentColors: [],
  imageManifest: [],
  schema: 1,
};

/**
 * Loads the page at `index` into `editorInit` (or clears it for non-editable
 * page types). Any reducer that changes which page is selected must call this
 * — otherwise the editor remounts (keyed on page id) but initializes from
 * stale content belonging to whatever page was previously loaded, and the
 * next edit silently overwrites the newly-selected page with it.
 */
function syncEditorInit(state: Draft<_TemplateBuilderState>, index: number) {
  const page = state.pages[index];
  if (page && (page.type === TemplatePageType.customPage || page.type === TemplatePageType.coverPage)) {
    state.editorInit = [...(page.content as any[])];
  } else {
    state.editorInit = [];
  }
}

export const templateBuilderSlice = createSlice({
  name: "template-builder-data",
  // `createSlice` will infer the state type from the `initialState` argument
  initialState,
  reducers: {
    loadTemplate: (
      state,
      action: PayloadAction<{ templateName: string; data?: TTemplateContent; logoUrl?: string; storageToken: string }>
    ) => {
      let templatePages: TSectionPage[] = [];

      if (state.templateName !== action.payload.templateName) {
        state.templateName = action.payload.templateName;
      }

      if (action.payload.logoUrl) {
        state.logoUrl = action.payload.logoUrl;
      }

      if (action.payload.storageToken) {
        state.storageToken = action.payload.storageToken;
      }

      if (action.payload.data) {
        const { defaultOptions, pages, settings, id, recentColors, imageManifest, schema } = action.payload.data;
        state.documentId = id;

        if (recentColors) {
          state.recentColors = recentColors;
        }

        if (pages) {
          templatePages = pages;
        }

        if (typeof schema === "number" && schema >= 1) {
          state.imageManifest = imageManifest ?? [];
          state.schema = schema;
        } else if (templatePages.length > 0) {
          // Legacy document saved before imageManifest existed — build one now from whatever
          // image references it already has, rather than deferring to the next save.
          const migrated = migrateLegacyImageReferences(templatePages);
          templatePages = migrated.pages;
          state.imageManifest = migrated.manifest;
          state.schema = 1;
        }

        if (defaultOptions) {
          const rawOptions = { ...defaultOptions } as Record<string, TDefectListOption>;

          // Legacy casing from a past SettingsKey rename: "reportShowDefectDueDate" ->
          // "reportShowDefectDuedate". Carry the saved value across before stripping the
          // stale key so an existing tenant's explicit toggle isn't silently lost.
          const legacyDuedate = rawOptions["reportShowDefectDueDate"];
          if (legacyDuedate && !rawOptions[SettingsKey.ReportShowDefectDuedate]) {
            rawOptions[SettingsKey.ReportShowDefectDuedate] = {
              ...legacyDuedate,
              key: SettingsKey.ReportShowDefectDuedate,
            };
          }

          // Strip any remaining legacy/unknown keys so they don't get carried forward
          // and re-saved forever.
          const validKeys = new Set<string>(Object.values(SettingsKey));
          const sanitizedOptions = Object.fromEntries(
            Object.entries(rawOptions).filter(([key]) => validKeys.has(key))
          ) as TDefectListOptions;

          state.defaultOptions = { ...blankDefectListOptions, ...sanitizedOptions };
        }

        if (settings?.reportHeader) {
          state.headerEditorInit = settings.reportHeader;
          state.settings.reportHeader = settings.reportHeader;
        }

        if (settings?.reportFooter) {
          state.footerEditorInit = settings.reportFooter;
          state.settings.reportFooter = settings.reportFooter;
        }
      }

      if (templatePages.length === 0) {
        // Brand new template — default header/footer + a guided starter document
        // (cover + About Us / Disclaimer onboarding sections).
        state.settings.reportHeader = defaultPageHeader;
        state.headerEditorInit = defaultPageHeader;
        state.settings.reportFooter = defaultPageFooter;
        state.footerEditorInit = defaultPageFooter;
        templatePages = seedNewTemplate();
        // Greet first, then prompt for a cover design — WelcomeModal's onHide opens this.
        state.showWelcome = true;
        state.showCoverPicker = true;
      }

      // Guarantee the three mandatory sections (cover / contents / defect list).
      templatePages = ensureMandatorySections(templatePages);

      if (templatePages.length > 0) {
        state.pages = templatePages;
        state.selectedPageIndex = 0;
        state.editorInit = templatePages[0].content;
      }
    },
    reorderPage: (state, action: PayloadAction<{ selectedPageIndex: number; pages: TSectionPage[] }>) => {
      state.pages = action.payload.pages;
      state.selectedPageIndex = action.payload.selectedPageIndex;
      syncEditorInit(state, action.payload.selectedPageIndex);
    },
    movePage: (state, action: PayloadAction<{ from: number; to: number }>) => {
      const { from, to } = action.payload;
      if (to < 1 || to >= state.pages.length) return;

      const reorderedList = [...state.pages];

      var element = reorderedList[from];
      reorderedList.splice(from, 1);
      reorderedList.splice(to, 0, element);

      // Validate contents page is before defect list page
      const indexOfCover = reorderedList.findIndex((o) => o.type === TemplatePageType.coverPage);
      const indexOfContents = reorderedList.findIndex((o) => o.type === TemplatePageType.contentsPage);
      const indexOfDefectList = reorderedList.findIndex((o) => o.type === TemplatePageType.defectList);

      if (indexOfCover !== 0 || indexOfContents > indexOfDefectList) return;

      state.pages = reorderedList;

      state.showSettings = false;

      if (to >= 0 && to < state.pages.length) {
        state.selectedPageIndex = to;
        syncEditorInit(state, to);
      }
    },
    selectPage(state, action: PayloadAction<number>) {
      const newIndex = action.payload;

      state.showSettings = false;

      if (newIndex >= 0 && newIndex < state.pages.length) {
        state.selectedPageIndex = newIndex;
        syncEditorInit(state, newIndex);
      }
    },
    selectSettingsPage(state) {
      // Opens as an overlay on top of the current page, so it doesn't touch
      // selectedPageIndex — there's nothing to "return to" otherwise.
      state.showSettings = true;
    },
    closeSettingsPage(state) {
      state.showSettings = false;
    },
    openCoverPicker(state) {
      state.showCoverPicker = true;
    },
    closeCoverPicker(state) {
      state.showCoverPicker = false;
    },
    closeWelcome(state) {
      state.showWelcome = false;
    },
    addPage(state, actions: PayloadAction<number | null>) {
      let index = actions.payload;

      const pages = [...state.pages];
      // Seed a one-line "delete me" tip so the section explains itself.
      const starter = customSectionStarter();
      const newPage: TSectionPage = {
        type: TemplatePageType.customPage,
        id: uuidv4(),
        content: starter,
      };

      if (index !== null) {
        // new page position is +1 of index
        index++;
        // custom page cannot be in front of cover sheet
        if (index < 1) index = 1;
        pages.splice(index, 0, newPage);
      } else {
        pages.push(newPage);
        index = pages.length - 1;
      }

      state.pages = pages;
      state.selectedPageIndex = Math.min(Math.max(0, index), pages.length - 1);
      state.editorInit = starter;
    },
    deletePage(state, action: PayloadAction<number>) {
      const index = action.payload;
      if (state.pages[index]) {
        const pages = [...state.pages];
        pages.splice(index, 1);
        const newPageSelection = Math.min(Math.max(0, index - 1), pages.length - 1);
        // update view
        state.pages = pages;
        state.selectedPageIndex = newPageSelection;
        syncEditorInit(state, newPageSelection);
      }
    },
    /** Inserts a copy of the custom section at `index` right after it, selected. Fixed sections
     * (cover/contents/defect list) are singletons managed by ensureMandatorySections and can't
     * be duplicated — the UI never offers this action for them, but guard here too. */
    duplicatePage(state, action: PayloadAction<number>) {
      const index = action.payload;
      const source = state.pages[index];
      if (!source || source.type !== TemplatePageType.customPage) return;

      const newIndex = index + 1;
      const newPage: TSectionPage = {
        type: source.type,
        id: uuidv4(),
        // Deep-cloned rather than sharing source.content's reference — the two pages must be
        // able to diverge independently from this point on.
        content: JSON.parse(JSON.stringify(source.content ?? [])),
      };

      const pages = [...state.pages];
      pages.splice(newIndex, 0, newPage);

      state.pages = pages;
      state.selectedPageIndex = newIndex;
      syncEditorInit(state, newIndex);
    },
    onContentChange(state, action: PayloadAction<{ type: "doc"; content: Content }>) {
      if (state.pages[state.selectedPageIndex]) {
        const doc = action.payload;
        (state.pages[state.selectedPageIndex] as any).content = doc.content ?? [];
      }
    },
    /**
     * Replace ONLY the cover page's content with a chosen design, leaving every
     * other section (and any custom pages) untouched. Selects the cover page and
     * refreshes editorInit so the new design renders immediately. Used by the
     * "Cover Design" picker as an easy restart-without-wrecking-everything path.
     */
    applyCoverDesign(state, action: PayloadAction<{ content: Content }>) {
      const coverIndex = state.pages.findIndex((o) => o.type === TemplatePageType.coverPage);
      if (coverIndex < 0) return;

      const content = (action.payload.content ?? []) as Content;
      (state.pages[coverIndex] as any).content = content;

      state.showSettings = false;
      state.showCoverPicker = false;
      state.selectedPageIndex = coverIndex;
      state.editorInit = Array.isArray(content) ? [...content] : content;
    },
    setOption(state, action: PayloadAction<{ key: SettingsKey; option: TDefectListOption }>) {
      // update or create new option
      state.defaultOptions[action.payload.key] = action.payload.option;
    },
    setHeaderContent(state, action: PayloadAction<{ type: "doc"; content: Content }>) {
      state.settings.reportHeader = action.payload.content;
    },
    setFooterContent(state, action: PayloadAction<{ type: "doc"; content: Content }>) {
      state.settings.reportFooter = action.payload.content;
    },
    setTemplateTitle(state, action: PayloadAction<string>) {
      state.templateName = action.payload;
    },
    setDocumentId(state, action: PayloadAction<string>) {
      if (state.documentId !== action.payload) {
        state.documentId = action.payload;
      }
    },
    setLoading(state, action: PayloadAction<boolean>) {
      state.loading = action.payload;
    },
    setSaving(state, action: PayloadAction<boolean>) {
      state.isSaving = action.payload;
    },
    /** Moves `color` to the front of the recent-colours list (deduped, capped at kMaxRecentColors). */
    addRecentColor(state, action: PayloadAction<string>) {
      const color = action.payload;
      if (!/^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(color)) return;

      const withoutColor = state.recentColors.filter(
        (c) => c.toLowerCase() !== color.toLowerCase()
      );
      state.recentColors = [color, ...withoutColor].slice(0, kMaxRecentColors);
    },
    addImageManifestEntry(state, action: PayloadAction<TImageManifestEntry>) {
      state.imageManifest = [
        ...state.imageManifest.filter((e) => e.id !== action.payload.id),
        action.payload,
      ];
    },
    removeImageManifestEntries(state, action: PayloadAction<string[]>) {
      const ids = new Set(action.payload);
      state.imageManifest = state.imageManifest.filter((e) => !ids.has(e.id));
    },
  },
});

export const {
  loadTemplate,
  selectPage,
  deletePage,
  duplicatePage,
  reorderPage,
  addPage,
  onContentChange,
  setOption,
  setTemplateTitle,
  selectSettingsPage,
  closeSettingsPage,
  openCoverPicker,
  closeCoverPicker,
  closeWelcome,
  setDocumentId,
  setLoading,
  setFooterContent,
  setHeaderContent,
  movePage,
  setSaving,
  applyCoverDesign,
  addRecentColor,
  addImageManifestEntry,
  removeImageManifestEntries,
} = templateBuilderSlice.actions;

export default templateBuilderSlice.reducer;
